import React, { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';
import { Menu, LogOut, User } from 'lucide-react';

export interface SidebarNavItem {
  path: string;
  label: string;
  icon: LucideIcon;
}

interface SidebarProps {
  navItems: SidebarNavItem[];
  logoText: string;
  logoAccent: string;
  userName: string;
  userRole: string;
  onLogout: () => void;
}

const STORAGE_KEY = 'sidebar:expanded';

export const Sidebar: React.FC<SidebarProps> = ({
  navItems,
  logoText,
  logoAccent,
  userName,
  userRole,
  onLogout,
}) => {
  const [expanded, setExpanded] = useState<boolean>(() => {
    if (typeof window === 'undefined') return true;
    return localStorage.getItem(STORAGE_KEY) !== 'false';
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, String(expanded));
  }, [expanded]);

  return (
    <aside
      className={`flex h-screen flex-col border-r border-[#252a38] bg-[#161920] transition-[width] duration-200 ease-in-out ${
        expanded ? 'w-[220px]' : 'w-[60px]'
      }`}
    >
      {/* ── Header: toggle + logo ──────────────────────────────── */}
      <div className="flex h-14 shrink-0 items-center gap-2.5 border-b border-[#252a38] px-3.5">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-label={expanded ? 'Collapse sidebar' : 'Expand sidebar'}
          className="shrink-0 rounded-md p-1 text-[#64748b] transition-colors hover:bg-[#1e2235] hover:text-[#e2e8f0]"
        >
          <Menu className="h-[18px] w-[18px]" />
        </button>
        {expanded && (
          <span className="whitespace-nowrap text-[15px] font-semibold text-[#e2e8f0]">
            {logoText} <strong className="text-[#3b82f6]">{logoAccent}</strong>
          </span>
        )}
      </div>

      {/* ── Nav items ──────────────────────────────────────────── */}
      <nav className="flex-1 overflow-y-auto py-2">
        {navItems.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.path}
              to={item.path}
              title={!expanded ? item.label : undefined}
              className={({ isActive }) =>
                `mx-2 my-0.5 flex items-center gap-2.5 rounded-lg px-3.5 py-2.5 text-[13px] font-medium whitespace-nowrap transition-all duration-150 ${
                  expanded ? '' : 'justify-center px-2.5'
                } ${
                  isActive
                    ? 'bg-[#1e2a4a] text-[#3b82f6]'
                    : 'text-[#64748b] hover:bg-[#1e2235] hover:text-[#e2e8f0]'
                }`
              }
            >
              <Icon className="h-[18px] w-[18px] shrink-0" />
              {expanded && <span>{item.label}</span>}
            </NavLink>
          );
        })}
      </nav>

      {/* ── Footer: user info + logout ─────────────────────────── */}
      <div className="mt-auto flex flex-col gap-1 border-t border-[#252a38] p-2">
        <div
          className={`flex min-h-[46px] items-center gap-2.5 rounded-lg p-2 ${
            expanded ? '' : 'justify-center'
          }`}
        >
          <div className="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-full border border-[#3b82f6] bg-[#1e2a4a] text-[#3b82f6]">
            <User className="h-4 w-4" />
          </div>
          {expanded && (
            <div className="flex min-w-0 flex-col leading-tight">
              <span className="truncate text-[13px] font-semibold text-[#e2e8f0]">
                {userName}
              </span>
              <span className="text-[10px] font-medium uppercase tracking-[0.08em] text-[#475569]">
                {userRole}
              </span>
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={onLogout}
          title={!expanded ? 'Đăng xuất' : undefined}
          className={`flex min-h-[36px] w-full items-center gap-2 rounded-lg px-2 py-2 text-[13px] text-[#64748b] transition-colors hover:bg-[#2d1515] hover:text-[#ef4444] ${
            expanded ? '' : 'justify-center'
          }`}
        >
          <LogOut className="h-4 w-4 shrink-0" />
          {expanded && <span>Đăng xuất</span>}
        </button>
      </div>
    </aside>
  );
};